// JSON-LD Schema library for SEO/GEO — structured data for AI knowledge graphs
// Reference: https://schema.org, https://developers.google.com/search/docs/appearance/structured-data

import { getBlogUrl, type BlogPageType } from "~/lib/blog-url";

const SITE_URL = "https://alchema.eu";
const ORG_NAME = "Alchema";
const ORG_LOGO = `${SITE_URL}/logo.webp`;

// ── Canonical social profiles (single source of truth) ───────────────────────
// The org `sameAs` list. KEEP THIS as the one place that owns brand social URLs
// so the JSON-LD entity graph and the footer UI link never drift apart.
//
// LinkedIn (KB-714): interim canonical is the LinkedIn *showcase* page, which
// resolves 200 directly. The bare `/company/alchema-eu` slug 301-redirects to
// this showcase (a cross-entity hop that produces an ambiguous/404-style entity
// signal for crawlers), so we point at the redirect *target* instead. Re-point
// this to an official `/company/<slug>` page once one is claimed (see the
// KB-714 `linkedin-entity-handoff` task document / re-point follow-up).
export const LINKEDIN_CANONICAL_URL =
  "https://www.linkedin.com/showcase/alchema-eu/";
export const GITHUB_ORG_URL = "https://github.com/Elit3-X";

export const ORG_SAME_AS: readonly string[] = [
  LINKEDIN_CANONICAL_URL,
  GITHUB_ORG_URL,
];

// ── Organization Schema ──────────────────────────────────────────────────────

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: ORG_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: ORG_LOGO,
      width: 512,
      height: 512,
    },
    sameAs: [...ORG_SAME_AS],
    legalName: "EliteX GbR",
    telephone: "+4915563148598",
    description:
      "AI-governed job application platform for European job seekers. Provides AI-assisted resume tailoring, career tools, and EU AI Act Article 50 transparency features with EU data processing practices described in our Privacy Policy, and human review before every submission.",
    foundingDate: "2026",
    founder: [
      {
        "@type": "Person",
        "@id": `${SITE_URL}/#person-vaisakhan`,
        name: "Vaisakhan Arackal Sanu",
      },
      {
        "@type": "Person",
        "@id": `${SITE_URL}/#person-chris`,
        name: "Chris Zanfir",
      },
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Koppoldstr. 1",
      addressLocality: "Aichach",
      postalCode: "86551",
      addressCountry: "DE",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "contact@alchema.eu",
      contactType: "customer support",
      availableLanguage: ["German", "English"],
    },
    areaServed: {
      "@type": "Place",
      name: "European Union",
    },
  };
}

// ── SoftwareApplication / Product shared data ───────────────────────────────

/**
 * Caller-supplied, verified AggregateRating values.
 *
 * There are deliberately no numeric defaults: a caller must provide the real
 * rating and review count, and optional scale bounds are emitted only when the
 * source explicitly supplies them.
 */
export interface AggregateRatingInput {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}

/**
 * Canonical Free / Student / Pro EUR offers.
 * Shared by SoftwareApplication and Product so public pricing cannot drift.
 */
export function pricingOffers() {
  return [
    {
      "@type": "Offer",
      name: "Free",
      price: "0",
      priceCurrency: "EUR",
      description:
        "5 AI actions/month, 1 resume, 10 job tracking slots, resume quality review checks",
      url: `${SITE_URL}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Student",
      price: "14.99",
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "14.99",
        priceCurrency: "EUR",
        unitText: "MONTH",
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: "1",
          unitCode: "MON",
        },
      },
      description:
        "75 AI actions/month, unlimited resumes, unlimited job tracking, full salary data, interview prep, company briefs, PDF/DOCX export, email support",
      url: `${SITE_URL}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "34.99",
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "34.99",
        priceCurrency: "EUR",
        unitText: "MONTH",
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: "1",
          unitCode: "MON",
        },
      },
      description:
        "300 AI actions/month, full analytics, salary negotiation, priority support",
      url: `${SITE_URL}/pricing`,
    },
  ];
}

// ── AggregateRating Schema ───────────────────────────────────────────────────

export function aggregateRatingSchema(input: AggregateRatingInput) {
  return {
    "@type": "AggregateRating",
    ratingValue: input.ratingValue,
    reviewCount: input.reviewCount,
    ...(input.bestRating !== undefined
      ? { bestRating: input.bestRating }
      : {}),
    ...(input.worstRating !== undefined
      ? { worstRating: input.worstRating }
      : {}),
  };
}

// ── SoftwareApplication Schema ───────────────────────────────────────────────

export function softwareAppSchema(
  options: {
    includeOffers?: boolean;
    aggregateRating?: AggregateRatingInput;
  } = {},
) {
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: ORG_NAME,
    url: SITE_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "AI-governed job application platform for European job seekers. Provides AI-assisted resume tailoring, cover letter generation, salary data, and interview preparation — with EU data security, AI governance controls, and human review before every final submission.",
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
    },
    featureList: [
      "AI resume tailoring",
      "Resume quality checks",
      "Cover letter generation",
      "Job matching",
      "Salary data by region",
      "Interview preparation",
      "User review before final submission",
      "AI governance controls",
      "EU data processing (see Privacy Policy for current provider and region details)",
      "EU AI Act Article 50 transparency features",
    ],
    inLanguage: ["de", "en"],
  };

  if (options.includeOffers) {
    base.offers = pricingOffers();
  }

  if (options.aggregateRating) {
    base.aggregateRating = aggregateRatingSchema(options.aggregateRating);
  }

  return base;
}

// ── Product Schema ───────────────────────────────────────────────────────────

export function productSchema(
  options: { aggregateRating?: AggregateRatingInput } = {},
) {
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE_URL}/#product`,
    name: "Alchema ATS-Check & Lebenslauf-Optimierung",
    description:
      "KI-gestützter ATS-Check und Lebenslauf-Optimierung für den DACH-Arbeitsmarkt: prüft deinen Lebenslauf auf ATS-Kompatibilität, verbessert Formulierungen und Struktur und erstellt passende Anschreiben. Datenverarbeitung gemäß aktueller Datenschutzerklärung (/privacy).",
    brand: {
      "@type": "Brand",
      name: ORG_NAME,
    },
    category: "SoftwareApplication",
    offers: pricingOffers(),
  };

  if (options.aggregateRating) {
    base.aggregateRating = aggregateRatingSchema(options.aggregateRating);
  }

  return base;
}

// ── LocalBusiness Schema ─────────────────────────────────────────────────────

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: ORG_NAME,
    legalName: "EliteX GbR",
    url: SITE_URL,
    telephone: "+4915563148598",
    email: "contact@alchema.eu",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Koppoldstr. 1",
      addressLocality: "Aichach",
      postalCode: "86551",
      addressCountry: "DE",
    },
    areaServed: ["DE", "AT", "CH"],
    priceRange: "€0–€34.99",
    inLanguage: ["de", "en"],
    parentOrganization: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
    },
  };
}

// ── FAQPage Schema ───────────────────────────────────────────────────────────

export function faqPageSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

// ── Author profiles for sameAs links ─────────────────────────────────────────

const AUTHOR_PROFILES: Record<string, string[]> = {
  "Vaisakhan Arackal Sanu": ["https://www.linkedin.com/in/vaisakhan-arackal-sanu/"],
  "Chris Zanfir": ["https://www.linkedin.com/in/chris-zanfir/"],
};

// ── Article Schema (BlogPosting with SpeakableSpecification) ─────────────────

export interface ArticleSchemaInput {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  author: string;
  locale: string;
  tags: string[];
  readingTimeMin: number;
  coverImage?: string;
  pageType?: BlogPageType | string;
  countryCode?: string | null;
}

export function articleSchema(post: ArticleSchemaInput) {
  const canonical = getBlogUrl({
    locale: post.locale,
    pageType: post.pageType ?? "editorial",
    slug: post.slug,
    countryCode: post.countryCode,
  });
  // Author: resolve to Organization for brand-attributed content (default)
  // or to a named Person when a real founder byline is used.
  const isPersonAuthor = post.author in AUTHOR_PROFILES;
  const author = isPersonAuthor
    ? {
        "@type": "Person" as const,
        "@id":
          post.author === "Vaisakhan Arackal Sanu"
            ? `${SITE_URL}/#person-vaisakhan`
            : `${SITE_URL}/#person-chris`,
        name: post.author,
        url: `${SITE_URL}/about`,
        sameAs: AUTHOR_PROFILES[post.author] ?? [],
      }
    : {
        "@type": "Organization" as const,
        "@id": `${SITE_URL}/#organization`,
        name: ORG_NAME,
        url: SITE_URL,
      };

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    author,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
      logo: {
        "@type": "ImageObject",
        url: ORG_LOGO,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical.absolute,
    },
    inLanguage: post.locale,
    keywords: post.tags.join(", "),
    wordCount: post.readingTimeMin * 200, // ~200 words/min estimate
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".blog-prose h2", ".blog-prose p:first-of-type"],
    },
    ...(post.coverImage
      ? {
          image: {
            "@type": "ImageObject",
            url: post.coverImage.startsWith("http")
              ? post.coverImage
              : `${SITE_URL}${post.coverImage}`,
          },
        }
      : {}),
  };
}

// ── Blog Listing Schema ──────────────────────────────────────────────────────

export function blogSchema(locale = "de", path?: string) {
  const canonical = path
    ? `${SITE_URL}${path}`
    : getBlogUrl({ locale, pageType: "index" }).absolute;
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": canonical,
    name: "Alchema Blog — Karriere-Tipps, Bewerbung & Gehalt",
    description:
      "Praxisnahe Karriere-Tipps: Lebenslauf erstellen, ATS-Score verbessern, Bewerbung schreiben und Gehalt verhandeln.",
    url: canonical,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
    },
    inLanguage: locale,
  };
}

// ── Breadcrumb Schema ────────────────────────────────────────────────────────

export function breadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

// ── WebPage Schema (with SpeakableSpecification) ─────────────────────────────

export function webPageSchema(page: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.name,
    description: page.description,
    url: page.url.startsWith("http") ? page.url : `${SITE_URL}${page.url}`,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2", "article > p:first-of-type"],
    },
  };
}

// ── WebSite Schema (with SearchAction for sitelinks search box) ──────────

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: ORG_NAME,
    url: SITE_URL,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
    },
    inLanguage: ["de", "en"],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

// ── Person Schema ────────────────────────────────────────────────────────

export function personSchema(person: {
  id: string;
  name: string;
  jobTitle: string;
  description: string;
  sameAs: string[];
  knowsAbout: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person" as const,
    "@id": `${SITE_URL}/#person-${person.id}`,
    name: person.name,
    jobTitle: person.jobTitle,
    description: person.description,
    worksFor: {
      "@type": "Organization" as const,
      "@id": `${SITE_URL}/#organization`,
      name: ORG_NAME,
    },
    sameAs: person.sameAs,
    knowsAbout: person.knowsAbout,
    url: `${SITE_URL}/about`,
  };
}

// ── HowTo Schema ────────────────────────────────────────────────────────

export function howToSchema(
  steps: Array<{ name: string; text: string }>,
  options?: { name?: string; description?: string },
) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: options?.name ?? "How to tailor your resume with Alchema",
    description:
      options?.description ??
      "Upload your resume, let AI tailor it to any job, and track your applications — in three simple steps.",
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

// ── Utility: Convert schema to JSON string for script tag ────────────────────

export function schemaToScript(schema: unknown): string {
  return JSON.stringify(schema);
}

// ── Utility: Escape JSON-LD for inline head injection ───────────────────────

/**
 * Escape only inline-script breakout sequences while preserving the value a
 * JSON-LD consumer receives from JSON.parse(). TanStack Router renders route
 * head-script children as raw script text, so these sequences must be
 * neutralized before they reach the document.
 */
export function serializeJsonLdForHead(json: string): string {
  return json
    .replace(/<\/(script)/gi, "\\u003c/$1")
    .replace(/<!--/g, "\\u003c!--");
}

// ── Landing page composite graph ─────────────────────────────────────────────

/**
 * Compose the landing entities as one Schema.org graph. AggregateRating stays
 * absent unless the caller supplies verified values explicitly.
 */
export function landingSchemaGraph(
  faqs: ReadonlyArray<{ question: string; answer: string }>,
  opts: { aggregateRating?: AggregateRatingInput } = {},
): string {
  const aggregateRatingOptions = opts.aggregateRating
    ? { aggregateRating: opts.aggregateRating }
    : {};

  return schemaGraph(
    organizationSchema(),
    websiteSchema(),
    softwareAppSchema({
      includeOffers: true,
      ...aggregateRatingOptions,
    }),
    productSchema(aggregateRatingOptions),
    localBusinessSchema(),
    faqPageSchema([...faqs]),
    howToSchema([
      {
        name: "Upload your resume",
        text: "Drop your existing resume or LinkedIn PDF. Alchema parses your experience, skills, and achievements instantly.",
      },
      {
        name: "AI tailors to the job",
        text: "Paste a job URL or description. Our AI rewrites your resume to match — keywords, formatting, and structure optimized for ATS.",
      },
      {
        name: "Apply and track",
        text: "Download your tailored resume, generate a cover letter, and track every application in one dashboard.",
      },
    ]),
  );
}

const REPLACED_BLOG_SCHEMA_TYPES = new Set([
  "BlogPosting",
  "Article",
  "NewsArticle",
  "BreadcrumbList",
]);

/** Keep useful stored entities while replacing stale canonical article/breadcrumb nodes. */
export function supplementalBlogSchemaNodes(
  storedSchema: Record<string, unknown> | null | undefined,
): Array<Record<string, unknown>> {
  if (!storedSchema) return [];
  const graph = Array.isArray(storedSchema["@graph"])
    ? storedSchema["@graph"] as Array<Record<string, unknown>>
    : [storedSchema];

  return graph.filter((node) => {
    const types = Array.isArray(node?.["@type"])
      ? node["@type"]
      : [node?.["@type"]];
    return !types.some((type) => typeof type === "string" && REPLACED_BLOG_SCHEMA_TYPES.has(type));
  });
}

// ── Utility: Render multiple schemas as a graph ──────────────────────────────

export function schemaGraph(...schemas: unknown[]): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": schemas.map((s) => {
      // Strip @context from individual schemas when merging into a graph
      if (
        typeof s === "object" &&
        s !== null &&
        "@context" in s
      ) {
        const { "@context": _, ...rest } = s as Record<string, unknown>;
        return rest;
      }
      return s;
    }),
  });
}
